import {ChangeDetectorRef, Directive, OnDestroy} from '@angular/core';
import {AbstractControl, UntypedFormGroup} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {NgxSpinnerService} from 'ngx-spinner';

import {BehaviorSubject, firstValueFrom, Observable, of, Subject} from 'rxjs';
import {filter, finalize, first, skip, switchMap, takeUntil} from 'rxjs/operators';

import {FormManagerService} from '@sim/form/services/form-manager.service';

import {Nullable, Undefinable} from '@atmi/core/models';

@Directive()
export abstract class BaseFormComponent<T> implements OnDestroy {

  readonly onDestroy$ = new Subject();
  readonly onFormInit$ = new BehaviorSubject<UntypedFormGroup | undefined>(undefined);

  readonly entityRoutePath = window.location.pathname;

  entityId: Undefinable<string> = this.route.snapshot.params.id;

  entityForm!: UntypedFormGroup;
  entityControls?: { [P in keyof T]: Nullable<AbstractControl> };

  formEditData?: T;

  readonly mode: 'edit' | 'add' = this.entityId ? 'edit' : 'add';

  isSubmit = false;

  resultForm?: any;
  /** Body of the email to sent via tools actions */
  entityEmailBody?: string;

  constructor(
    protected readonly snackBarService: MatSnackBar,
    protected readonly translate: TranslateService,
    protected readonly route: ActivatedRoute,
    protected readonly formManager: FormManagerService,
    // TODO: Allow to set a generic spinner with an Adapter interface as Material do for DateAdapter
    protected readonly spinner: NgxSpinnerService,
    protected readonly cd?: ChangeDetectorRef,
    protected readonly formMeta?: any, // FIXME: Improve type
    protected readonly formEditDataExtractor: (id?: string) => Observable<T | undefined> = () => of(undefined),
    protected readonly indexResourcePath?: string,
    protected readonly router?: Router,
    protected readonly queryParams = {},
  ) {
    this.route.params.pipe(
      skip(1),
      filter((params) => !!params.id),
      takeUntil(this.onDestroy$)
    ).subscribe((params) => {
      this.entityId = params.id;

      this.init(this.formMeta);
    });

    this.init(this.formMeta);
  }

  async init(formMeta: any) {
    if (this.formEditDataExtractor && this.entityId) {
      this.spinner.show();

      try {
        this.formEditData = await firstValueFrom(this.formEditDataExtractor(this.entityId).pipe(first()));
        console.log('Form edit data loaded:', this.formEditData);
      } catch (e) {
        console.error('Error loading form data:', e);
        this.snackBarService.open(
          this.translate.instant('Errore durante il caricamento dei dati') + ': ' + (e as any).message,
          undefined,
          { duration: 5000 }
        );
        this.spinner.hide();
        return;
      }
    }

    if (this.formMeta) {
      this.entityForm = this.formManager.makeForm(formMeta, this.formEditData);
      this.entityControls = this.formManager.getControls(this.entityForm);
      console.log('Form created:', this.entityForm.value);

      if (this.cd) {
        this.cd.markForCheck();
      }
      this.spinner.hide();
    }

    this.onFormInit$.next(this.entityForm);
  }

  ngOnDestroy() {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();

    this.onFormInit$.complete();
  }

  remove(value: T) {
    this.removeConcrete(value).subscribe(() => {
      const msg = this.translate.instant('Eliminazione avvenuta con successo');

      this.snackBarService.open(msg, undefined, {duration: 3000});

      if (this.router && this.indexResourcePath) {
        this.router.navigate([this.indexResourcePath]);
      }
    });
  }

  dispatchAction(form: UntypedFormGroup, mode?: 'add' | 'edit') {
    mode = mode || this.mode;

    this.isSubmit = true;

    if (!form.valid) {
      form.markAllAsTouched();
      return;
    }
    of(mode).pipe(
      switchMap((type) => {
        return type === 'add'
          ? this.add(form.value)
          : this.edit({...this.formEditData, ...form.value});
      }),
      finalize(() => {
        this.isSubmit = false;
      }),
    ).subscribe((data) => {
      const msg = this.mode === 'add'
        ? this.translate.instant('Inserimento avvenuto con successo')
        : this.translate.instant('Modifica avvenuta con successo');

      this.snackBarService.open(msg, undefined, {duration: 3000});
// @ts-ignore
      if (this.router && this.indexResourcePath && this.mode === 'add' && 'id' in data) {
        this.router.navigate(['.', (data as any).id],
          {queryParams: this.queryParams, queryParamsHandling: 'merge', relativeTo: this.route});
      }
    });
  }

  private add(value: T) {
    return this.addConcrete(value);
  }

  private edit(value: T) {
    return this.editConcrete(value);
  }

  private resetForm() {
    return this.resetFormConcrete();
  }

  abstract removeConcrete(value: T): Observable<any>; // Template method pattern
  abstract addConcrete(value: T): Observable<T>;

  abstract editConcrete(value: T): Observable<T>;

  abstract resetFormConcrete(): void;
}
